import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import withConfig from '../../wrappers/withConfig'
import toast from '../../elem/Toast'
import { APIRequestContext } from '../../wrappers/APIRequestContext'
import { PSAPDetailContext } from '../../wrappers/PSAPDetailContext'

const EditPSAPForm = ({ config, handleSave, saving, savingPSAP }) => {
    const { API_URL } = config
    const { authenticatedFetch } = useContext(APIRequestContext)
    const { PSAP, setPSAP } = useContext(PSAPDetailContext) // assuming setter exists
    const { register, handleSubmit, errors, reset } = useForm()
    const [response, setResponse] = useState(null)

    // Initialize form with current PSAP values
    useEffect(() => {
        if (PSAP) {
            reset({
                CoordinatorName: PSAP.CoordinatorName || '',
                CoordinatorEmail: PSAP.CoordinatorEmail || '',
                NotificationDelay:
                    PSAP.NotificationDelay !== null &&
                    PSAP.NotificationDelay !== undefined
                        ? PSAP.NotificationDelay
                        : '',
                NotificationInterval:
                    PSAP.NotificationInterval !== null &&
                    PSAP.NotificationInterval !== undefined
                        ? PSAP.NotificationInterval
                        : '',
            })
        }
    }, [PSAP, reset])

    useEffect(() => {
        if (response) {
            handleSave(response)
        }
    }, [response, handleSave])

    const PUT = (data) => ({
        method: 'PUT',
        mode: 'cors',
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers':
                'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
        },
        body: JSON.stringify({
            Id: PSAP.Id,
            CoordinatorName: data.CoordinatorName,
            CoordinatorEmail: data.CoordinatorEmail,
            NotificationDelay:
                data.NotificationDelay === '' ? null : Number(data.NotificationDelay),
            NotificationInterval:
                data.NotificationInterval === '' ? null : Number(data.NotificationInterval),
        }),
    })

    const onSubmit = (data) => {
        savingPSAP(true)

        authenticatedFetch(`${API_URL}/psap/${PSAP.Id}`, PUT(data))
            .then(async (resp) => {
                if (resp.ok) {
                    const updated = await resp.json()
                    // Update PSAP in context if you want immediate UI refresh
                    if (setPSAP) {
                        setPSAP(updated)
                    }
                    setResponse({ ok: true })
                } else {
                    const error = await resp.text()
                    throw new Error(error)
                }
            })
            .catch((e) => {
                toast({
                    level: 'error',
                    message:
                        'Edit PSAP: ' +
                        (e.message ? e.message : 'Saving PSAP details failed'),
                })
                setResponse({ ok: false })
            })
            .finally(() => {
                savingPSAP(false)
            })
    }

    return (
        <form
            id="editPSAPForm"
            onSubmit={handleSubmit(onSubmit)}
            className="form"
        >
            {/* Editable fields */}
            <div className="field">
                <label className="label">Coordinator Name</label>
                <div className="control">
                    <input
                        name="CoordinatorName"
                        className="input"
                        type="text"
                        ref={register({
                            required: {
                                value: true,
                                message: 'Coordinator name is required',
                            },
                        })}
                        disabled={saving}
                    />
                </div>
                {errors.CoordinatorName && (
                    <p className="help is-danger">
                        {errors.CoordinatorName.message}
                    </p>
                )}
            </div>

            <div className="field">
                <label className="label">Coordinator Email</label>
                <div className="control">
                    <input
                        name="CoordinatorEmail"
                        className="input"
                        type="email"
                        ref={register({
                            required: {
                                value: true,
                                message: 'Coordinator email is required',
                            },
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Please enter a valid email address',
                            },
                        })}
                        disabled={saving}
                    />
                </div>
                {errors.CoordinatorEmail && (
                    <p className="help is-danger">
                        {errors.CoordinatorEmail.message}
                    </p>
                )}
            </div>

            <div className="field">
                <label className="label">Notification Delay (days)</label>
                <div className="control">
                    <input
                        name="NotificationDelay"
                        className="input"
                        type="number"
                        min="0"
                        ref={register({
                            min: {
                                value: 0,
                                message: 'Delay cannot be negative',
                            },
                        })}
                        disabled={saving}
                    />
                </div>
                {errors.NotificationDelay && (
                    <p className="help is-danger">
                        {errors.NotificationDelay.message}
                    </p>
                )}
            </div>

            <div className="field">
                <label className="label">Notification Interval (days)</label>
                <div className="control">
                    <input
                        name="NotificationInterval"
                        className="input"
                        type="number"
                        min="0"
                        ref={register({
                            min: {
                                value: 0,
                                message: 'Interval cannot be negative',
                            },
                        })}
                        disabled={saving}
                    />
                </div>
                {errors.NotificationInterval && (
                    <p className="help is-danger">
                        {errors.NotificationInterval.message}
                    </p>
                )}
            </div>

            {/* Readonly fields */}
            <div className="field">
                <label className="label">Last Notification Sent</label>
                <input
                    className="input"
                    type="text"
                    value={
                        PSAP.LastNotificationSent
                            ? new Date(PSAP.LastNotificationSent).toLocaleString()
                            : 'Never'
                    }
                    readOnly
                />
            </div>
        </form>
    )
}

export default withConfig(EditPSAPForm)
